/**
 * 新手引导（M5-05 / DESIGN.md §7.5）。
 *
 * **不是模态对话框**——DESIGN.md §7.5 要求的"计算中不阻塞 UI"同一条精神
 * 延伸到这里：引导展示期间画布必须仍然可以正常编辑，所以这里没有全屏
 * 遮罩，只是浮在真实界面之上的一圈高亮框 + 一张小提示卡片，两者都
 * `pointer-events: none`（提示卡片本身的按钮除外），底下的工具箱/画布
 * 照常能点。
 *
 * 每一步指向一个真实存在的 UI 地标（用 CSS 选择器 + `getBoundingClientRect`
 * 量出真实屏幕位置，不是写死的坐标——布局变了这里不用跟着改）。
 */
import { Show, createEffect, createSignal, onCleanup } from 'solid-js';
import type { JSX } from 'solid-js';
import styles from './OnboardingTour.module.css';

export interface TourStep {
  /** 要高亮的元素的 CSS 选择器。找不到就跳过这一步（比如引导跑在某个
   * 面板还没挂载完成的中间态），不阻塞整个引导流程。 */
  readonly target: string;
  readonly title: string;
  readonly body: string;
}

export interface OnboardingTourProps {
  readonly open: boolean;
  readonly steps: readonly TourStep[];
  readonly onSkip: () => void;
  readonly onFinish: () => void;
}

interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

function measure(selector: string): Rect | null {
  const el = document.querySelector(selector);
  if (el === null) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null; // 存在但没有布局尺寸，视同不存在
  return { x: r.x, y: r.y, width: r.width, height: r.height };
}

/**
 * 提示卡片放在高亮框哪一侧：依次试下方/上方/右侧/左侧，挑第一个能在
 * 视口内完整放下的（不裁切）。真实踩过的坑：工具箱这类贴着屏幕一条边、
 * 几乎跟视口一样高的窄长面板，"下方/上方"两个方向都放不下完整的提示
 * 卡片（上下都没有 148px 高的余量），早期版本因此退化成钉在视口左上角，
 * 结果卡片正好摞在画布左上角上面——e2e 测"引导展示期间画布仍可编辑"时
 * 精确复现（`document.elementFromPoint` 量出来命中的是提示卡片而不是
 * 画布）。四个方向都试一遍就不会有这个退化：工具箱这种情况会命中"右侧"
 * （目标窄，右边永远有空间），不会退到"贴视口角落"这个兜底。 */
function placeTooltip(target: Rect, tooltipSize: { width: number; height: number }): { x: number; y: number } {
  const margin = 12;
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const candidates = [
    { x: target.x, y: target.y + target.height + margin }, // 下方
    { x: target.x, y: target.y - tooltipSize.height - margin }, // 上方
    { x: target.x + target.width + margin, y: target.y }, // 右侧
    { x: target.x - tooltipSize.width - margin, y: target.y }, // 左侧
  ];
  const fitsViewport = (p: { x: number; y: number }): boolean =>
    p.x >= 0 &&
    p.y >= 0 &&
    p.x + tooltipSize.width <= viewport.width &&
    p.y + tooltipSize.height <= viewport.height;
  const chosen = candidates.find(fitsViewport) ?? candidates[0]!;
  return {
    x: Math.min(Math.max(margin, chosen.x), viewport.width - tooltipSize.width - margin),
    y: Math.min(Math.max(margin, chosen.y), viewport.height - tooltipSize.height - margin),
  };
}

export function OnboardingTour(props: OnboardingTourProps): JSX.Element {
  const [index, setIndex] = createSignal(0);
  const [targetRect, setTargetRect] = createSignal<Rect | null>(null);
  let tooltipRef: HTMLDivElement | undefined;
  const [tooltipPos, setTooltipPos] = createSignal({ x: 0, y: 0 });

  const currentStep = (): TourStep | undefined => props.steps[index()];

  const next = (): void => {
    if (index() + 1 >= props.steps.length) {
      props.onFinish();
    } else {
      setIndex((i) => i + 1);
    }
  };

  const reposition = (): void => {
    const step = currentStep();
    if (step === undefined) return;
    const rect = measure(step.target);
    // 目标地标这次没量到（面板还没挂载完/布局临时是 0 尺寸）就不卡住
    // 引导——直接跳到下一步，跟点"下一步"走同一条路径，而不是留一个
    // 找不到高亮目标的空提示卡片悬在屏幕上
    if (rect === null) {
      next();
      return;
    }
    setTargetRect(rect);
    if (tooltipRef === undefined) return;
    const size = tooltipRef.getBoundingClientRect();
    setTooltipPos(placeTooltip(rect, { width: size.width, height: size.height }));
  };

  createEffect(() => {
    if (!props.open) return;
    index(); // 依赖：切步骤要重新量
    queueMicrotask(reposition);
  });

  createEffect(() => {
    if (!props.open) return;
    window.addEventListener('resize', reposition);
    onCleanup(() => window.removeEventListener('resize', reposition));
  });

  return (
    <Show when={props.open && currentStep() !== undefined}>
      <div class={styles.root} data-testid="onboarding-tour">
        <Show when={targetRect()}>
          {(rect) => (
            <div
              class={styles.ring}
              style={{
                left: `${rect().x - 4}px`,
                top: `${rect().y - 4}px`,
                width: `${rect().width + 8}px`,
                height: `${rect().height + 8}px`,
              }}
              data-testid="onboarding-ring"
            />
          )}
        </Show>
        <div
          ref={(el) => {
            tooltipRef = el;
          }}
          class={styles.tooltip}
          style={{ left: `${tooltipPos().x}px`, top: `${tooltipPos().y}px` }}
          data-testid="onboarding-tooltip"
        >
          <p class={styles.progress}>
            {index() + 1} / {props.steps.length}
          </p>
          <h3 class={styles.title}>{currentStep()!.title}</h3>
          <p class={styles.body}>{currentStep()!.body}</p>
          <div class={styles.actions}>
            <button type="button" class={styles.skip} onClick={props.onSkip} data-testid="onboarding-skip">
              跳过
            </button>
            <button type="button" class={styles.next} onClick={next} data-testid="onboarding-next">
              {index() + 1 >= props.steps.length ? '知道了' : '下一步'}
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
